import { Fragment, Mark, MarkType, Node, NodeType, Slice } from "prosemirror-model";
import { Transform } from "prosemirror-transform";
import Delta from "quill-delta";

import { mergeAdjacentCodeBlocks } from "../utils";

import { schema } from "./schema";

const noAttrs: Record<string, unknown> = {};

function marksFromAttributes(attrs: Record<string, unknown>) {
  let marks = Mark.none;
  if (attrs.bold) marks = schema.marks.bold.create().addToSet(marks);
  if (attrs.italic) marks = schema.marks.italic.create().addToSet(marks);
  if (attrs.underline) marks = schema.marks.underline.create().addToSet(marks);
  if (attrs.color) marks = schema.marks.color.create({ color: attrs.color }).addToSet(marks);
  if (attrs.link) marks = schema.marks.link.create({ href: attrs.link }).addToSet(marks);
  return marks;
}

function readMarkUpdate(attrs: Record<string, unknown>) {
  const add = marksFromAttributes(attrs);
  const remove: MarkType[] = [];
  for (const name of Object.keys(attrs))
    if (attrs[name] === null || attrs[name] === undefined) {
      if (name === "bold") remove.push(schema.marks.bold);
      if (name === "italic") remove.push(schema.marks.italic);
      if (name === "underline") remove.push(schema.marks.underline);
      if (name === "color") remove.push(schema.marks.color);
      if (name === "link") remove.push(schema.marks.link);
    }
  return { add, remove };
}

function inlineToNode(insert: string | object, attributes: Record<string, unknown>) {
  const marks = marksFromAttributes(attributes);
  if (typeof insert === "string") return schema.text(insert, marks);
  if ("image" in insert)
    return schema.nodes.image.create(
      {
        src: insert.image,
        width: attributes.width && Number(attributes.width),
        alt: attributes.alt,
      },
      null,
      marks,
    );
  if ("formula" in insert)
    return schema.nodes.formula.create({ formula: insert.formula }, null, marks);
  throw new Error(`Unrecognized node in delta: ${JSON.stringify(insert)}`);
}

function blockFromAttributes(attrs: Record<string, unknown>): {
  type: NodeType;
  attrs: Record<string, unknown>;
} {
  const n = schema.nodes;
  let type = n.paragraph;
  const nodeAttrs: Record<string, unknown> = {};
  if (attrs.blockquote) type = n.quote;
  else if (attrs.header) {
    type = n.heading;
    nodeAttrs.level = attrs.header;
  } else if (attrs.list === "ordered") type = n.ordered_list_item;
  else if (attrs.list === "bullet") type = n.bullet_list_item;
  else if (attrs["code-block"]) type = n.code_block;
  if (attrs.indent) nodeAttrs.indent = attrs.indent;
  if (attrs.align) nodeAttrs.align = attrs.align;
  return { type, attrs: nodeAttrs };
}

function readBlockUpdate(attrs: Record<string, unknown>, block: Node) {
  let type = block.type;
  const blockAttrs = { ...block.attrs };
  const n = schema.nodes;
  for (const name of Object.keys(attrs)) {
    const val = attrs[name];
    if (name === "blockquote") {
      if (val !== null) type = n.quote;
      else if (type === n.quote) type = n.paragraph;
    } else if (name === "header") {
      if (val !== null) {
        type = n.heading;
        blockAttrs.level = val;
      } else if (type === n.heading) type = n.paragraph;
    } else if (name === "list") {
      if (val === "ordered") type = n.ordered_list_item;
      else if (val === "bullet") type = n.bullet_list_item;
      else if (type === n.bullet_list_item || type === n.ordered_list_item) type = n.paragraph;
    } else if (name === "code-block") {
      if (val !== null) type = n.code_block;
      else if (type === n.code_block) type = n.paragraph;
    } else if (name === "align") {
      if (val !== null) blockAttrs.align = val;
      else blockAttrs.align = undefined;
    } else if (name === "indent") {
      if (val !== null) blockAttrs.indent = val;
      else blockAttrs.indent = undefined;
    }
  }
  return { type, attrs: blockAttrs };
}

export function deltaToDoc(delta: Delta) {
  const blocks: Node[] = [];
  // Consecutive delta lines with code-block attrs → one PM code_block with embedded \n.
  let codeBlockContent: Node[] | null = null;
  let codeBlockAttrs: Record<string, unknown> | null = null;
  const flushCodeBlock = () => {
    if (codeBlockContent === null) return;
    blocks.push(schema.nodes.code_block.create(codeBlockAttrs, codeBlockContent));
    codeBlockContent = null;
    codeBlockAttrs = null;
  };
  delta.eachLine((inline, blockAttrs) => {
    const { type, attrs } = blockFromAttributes(blockAttrs);
    const lineNodes = inline.ops.map((op) => inlineToNode(op.insert!, op.attributes || noAttrs));
    if (type === schema.nodes.code_block) {
      if (codeBlockContent !== null) {
        codeBlockContent.push(schema.text("\n"));
      } else {
        codeBlockContent = [];
      }
      codeBlockContent.push(...lineNodes);
      if (!codeBlockAttrs) codeBlockAttrs = attrs;
      return;
    }
    flushCodeBlock();
    blocks.push(type.create(attrs, lineNodes));
  });
  flushCodeBlock();
  return schema.nodes.doc.create(null, blocks);
}

type BlockToken = { openBlock: NodeType; attrs: Record<string, unknown> };
type Token = Node | BlockToken;
type Change = {
  from: number;
  to: number;
  // A change is either an inline mark change, with addMarks and
  // removeMarks properties, a block attribute change with an
  // attrChange property, or a replacement, with an insert property.
  addMarks?: readonly Mark[];
  removeMarks?: readonly MarkType[];
  attrChange?: Record<string, unknown>;
  insert?: Token[];
};

function attrPatch(from: Record<string, unknown>, to: Record<string, unknown>) {
  let patch: Record<string, unknown> | null = null;
  for (const name of Object.keys(to))
    if (from[name] !== to[name]) {
      if (!patch) patch = {};
      patch[name] = to[name];
    }
  return patch;
}

// First pass over a delta, which maps from delta positions into
// ProseMirror-style positions, and handles the awkward postfix block
// markup positions by moving changes to newline tokens to the start
// of the block.
//
// This will return a change with to == doc.content.size + 1 if the
// line break at the end of the document is replaced. deltaToSteps
// corrects for this.
function preprocessDelta(doc: Node, delta: Delta): Change[] {
  // Track the position (index of current block, offset into block,
  // full position) in the old document
  let docIndex = 0;
  let blockOff = 0;
  let blockPos = 0;
  let docPos = 1;
  let blockTouched = false;
  // Advance by N delta-based units
  const advanceDoc = (
    nParam: number,
    step?: (from: number, to: number, blockEnd: Node | null) => void,
  ) => {
    let n = nParam;
    while (n > 0) {
      const cur = doc.child(docIndex);
      const left = cur.content.size - blockOff;
      if (left >= n) {
        if (step) step(docPos, docPos + n, null);
        blockOff += n;
        docPos += n;
        break;
      } else {
        const blockEnd = docPos + left;
        if (step) {
          step(docPos, blockEnd, null);
          step(blockEnd, blockEnd + 1, cur);
        }
        docPos = blockEnd + 2;
        blockOff = 0;
        docIndex += 1;
        blockPos = docPos - 1;
        blockTouched = false;
        n -= left + 1;
      }
    }
    return docPos;
  };

  const changes: Change[] = [];
  const curReplaceChange = (): { from: number; to: number; insert: Token[] } => {
    const last = changes.length ? changes[changes.length - 1] : null;
    if (last && last.insert && last.to === docPos)
      return last as { from: number; to: number; insert: Token[] };
    const newLast = { from: docPos, to: docPos, insert: [] };
    changes.push(newLast);
    return newLast;
  };
  const patchBlock = (type: NodeType, attrs: Record<string, unknown>) => {
    let pos = blockPos;
    let docI = docIndex;
    let i = changes.length;
    for (; i > 0; i -= 1) {
      const change = changes[i - 1];
      if (change.to <= pos) break;
      if (change.insert) {
        for (let j = change.insert.length - 1; j >= 0; j -= 1) {
          if ("openBlock" in change.insert[j]) {
            change.insert[j] = { openBlock: type, attrs };
            return;
          }
        }
        while (change.from <= pos) {
          docI -= 1;
          const prevBlock = doc.child(docI);
          pos -= prevBlock.nodeSize;
        }
      }
    }
    if (docI >= doc.childCount) return; // No block to patch
    const oldBlock = doc.child(docI);
    if (oldBlock.type === type) {
      const patch = attrPatch(oldBlock.attrs, attrs);
      if (patch) changes.splice(i, 0, { from: pos, to: pos + 1, attrChange: patch });
    } else {
      changes.splice(i, 0, { from: pos, to: pos + 1, insert: [{ openBlock: type, attrs }] });
    }
  };

  for (let opIndex = 0; opIndex < delta.ops.length; opIndex += 1) {
    const op = delta.ops[opIndex];
    if (op.insert) {
      const change = curReplaceChange();
      if (typeof op.insert === "string") {
        for (let strIndex = 0; strIndex < op.insert.length; ) {
          const nextNL = op.insert.indexOf("\n", strIndex);
          if (nextNL === strIndex) {
            strIndex += 1;
            const block = blockFromAttributes(op.attributes || noAttrs);
            patchBlock(block.type, block.attrs);
            change.insert.push({ openBlock: schema.nodes.paragraph, attrs: noAttrs });
          } else {
            const end = nextNL < 0 ? op.insert.length : nextNL;
            change.insert.push(
              inlineToNode(op.insert.slice(strIndex, end), op.attributes || noAttrs),
            );
            strIndex = end;
          }
        }
      } else {
        change.insert.push(inlineToNode(op.insert, op.attributes || noAttrs));
      }
      blockTouched = true;
    } else if (op.delete) {
      curReplaceChange().to = advanceDoc(op.delete);
      blockTouched = true;
    } else if (
      typeof op.retain === "number" &&
      op.attributes &&
      Object.keys(op.attributes).length
    ) {
      const currentOp = op;
      advanceDoc(op.retain, (from, to, block) => {
        if (block) {
          const { type, attrs } = readBlockUpdate(currentOp.attributes!, block);
          patchBlock(type, attrs);
        } else {
          const { add, remove } = readMarkUpdate(currentOp.attributes!);
          if (add.length || remove.length)
            changes.push({ from, to, addMarks: add, removeMarks: remove });
          if ("width" in currentOp.attributes!)
            changes.push({
              from,
              to: from + 1,
              attrChange: {
                width: currentOp.attributes.width && Number(currentOp.attributes.width),
              },
            });
          if ("alt" in currentOp.attributes!)
            changes.push({
              from,
              to: from + 1,
              attrChange: {
                alt: currentOp.attributes.alt,
              },
            });
        }
      });
    } else if (typeof op.retain === "number") {
      const currentBlockTouched = blockTouched;
      advanceDoc(op.retain, (from, to, block) => {
        if (currentBlockTouched && block) patchBlock(block.type, block.attrs);
      });
    }
  }
  if (blockTouched && docPos <= doc.content.size) {
    const block = doc.child(docIndex);
    patchBlock(block.type, block.attrs);
  }
  return changes;
}

function buildSlice(tokens: readonly Token[], dropLast: boolean) {
  let curBlock: { type: NodeType; attrs: Record<string, unknown>; children: Node[] } = {
    type: schema.nodes.paragraph,
    attrs: noAttrs,
    children: [],
  };
  const blocks: Node[] = [];
  for (const token of tokens) {
    if ("openBlock" in token) {
      blocks.push(curBlock.type.create(curBlock.attrs, curBlock.children));
      curBlock = { type: token.openBlock, attrs: token.attrs, children: [] };
    } else {
      curBlock.children.push(token);
    }
  }
  if (!dropLast) blocks.push(curBlock.type.create(curBlock.attrs, curBlock.children));
  return new Slice(Fragment.from(blocks), 1, dropLast ? 0 : 1);
}

export function deltaToSteps(tr: Transform, delta: Delta) {
  const { doc } = tr;
  const changes = preprocessDelta(doc, delta);
  for (let i = changes.length - 1; i >= 0; i -= 1) {
    const { from, to: toParam, insert, addMarks, removeMarks, attrChange } = changes[i];
    if (insert) {
      let to = toParam;
      const end = to > doc.content.size;
      if (end) {
        to -= 1;
        if (from > doc.content.size) {
          // Beyond the end of the document, insert the new content
          tr.replace(doc.content.size, doc.content.size, buildSlice(insert, false));
        } else if (doc.resolve(from - 1).depth === 0 && !insert.length) {
          // At the root of the document and no content to insert, clear all content
          tr.replace(from - 1, to, Slice.empty);
        } else {
          // Replace the content between from and to with the new content
          tr.replace(from, to, buildSlice(insert, end));
        }
      } else {
        tr.replace(from, to, buildSlice(insert, end));
      }
    } else if (addMarks) {
      for (const add of addMarks) tr.addMark(from, toParam, add);
      for (const rm of removeMarks!) tr.removeMark(from, toParam, rm);
    } else if (attrChange) {
      for (const name of Object.keys(attrChange)) tr.setNodeAttribute(from, name, attrChange[name]);
    }
  }
  mergeAdjacentCodeBlocks(tr);
  return tr;
}
